import { ChevronDown, ChevronRight, LogOut, Menu } from 'lucide-react';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition
} from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import navigation, {
  NavigationChild,
  NavigationItem
} from './navigationConfig';
import { IMAGE_PATHS } from '@/constants/imagePaths';
import { APP_BASE_URL, ROLE_SLUG, ROUTE_PATHS } from '@/constants';
import { COLLAPSE_SIDEBAR_ROUTES } from '@/constants/paths';
import { useLogoutMutation } from '@/services';
import { idbStorage } from '@/utils/idbStorage';
import useAuthStore, { resetState } from '@/stores/authStore';

const Sidebar: React.FC<{ isOpen?: boolean; onClose?: () => void }> = ({
  isOpen = true,
  onClose
}) => {
  const location = useLocation();
  const userInfo = useAuthStore(state => state.userInfo);
  const roleData = userInfo?.user?.role as unknown;
  const roleSlug =
    (typeof roleData === 'object' && roleData !== null
      ? (roleData as { slug?: string }).slug
      : null) ?? undefined;

  const { mutateAsync: LogOutMutation } = useLogoutMutation();
  const [, starTransition] = useTransition();
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [flyoutPos, setFlyoutPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const hoverCloseTimer = useRef<number | null>(null);

  const filteredNavigation = useMemo(() => {
    if (roleSlug === ROLE_SLUG?.MOUNTAIN_MANAGEMENT) {
      const allowedItems = new Set(['Report Manager']);

      return navigation.reduce<NavigationItem[]>((acc, item) => {
        if (!allowedItems.has(item.name)) {
          return acc;
        }

        if (item.name === 'Report Manager') {
          const transportChild = item.children?.find(
            child => child.name === 'Transport Pax Report'
          );

          if (!transportChild) {
            return acc;
          }

          acc.push({
            ...item,
            children: [transportChild]
          });
          return acc;
        }

        if (item.name === 'Profile') {
          const profileChildren =
            item.children?.filter(child => child.name !== 'Configuration') ??
            [];

          acc.push({
            ...item,
            children: profileChildren
          });
          return acc;
        }

        acc.push(item);
        return acc;
      }, []);
    }

    if (roleSlug === ROLE_SLUG?.INSTRUCTOR) {
      const allowedItems = new Set(['Daily Roster', 'Transport Roster']);

      return navigation.reduce<NavigationItem[]>((acc, item) => {
        if (!allowedItems.has(item.name)) {
          return acc;
        }

        if (item.name === 'Profile') {
          const profileChildren =
            item.children?.filter(child => child.name !== 'Configuration') ??
            [];

          acc.push({
            ...item,
            children: profileChildren
          });
          return acc;
        }

        acc.push(item);
        return acc;
      }, []);
    }

    // Default case: if no specific role filtering applies, return all navigation items
    return navigation;
  }, [roleSlug]);

  // Collapse by default on specific routes and auto-expand parent item for active child
  useEffect(() => {
    // Collapse toggle by route match (prefix matching)
    const shouldCollapse = COLLAPSE_SIDEBAR_ROUTES.some(prefix =>
      location.pathname.startsWith(prefix)
    );
    setIsCollapsed(shouldCollapse);

    const activeParent = filteredNavigation.find((item: NavigationItem) =>
      item.children?.some((child: NavigationChild) =>
        location.pathname.startsWith(child.href)
      )
    );

    setExpandedItem(activeParent ? activeParent.name : null);
  }, [filteredNavigation, location.pathname]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItem(prev => (prev === itemName ? null : itemName));
  };

  const handleLogout = async () => {
    starTransition(async () => {
      const res = await LogOutMutation();
      const { success } = res;

      if (success) {
        resetState();
        await idbStorage.removeItem('auth-store');
        window.location.reload();
      }
    });
  };

  // Responsive: show/hide sidebar on mobile and collapse on desktop
  const widthClass = 'w-64'; // Always full width on mobile
  const mdWidthClass = isCollapsed ? 'md:w-16' : 'md:w-64';
  const sidebarClasses = `
    bg-white backdrop-blur-xl border-r border-slate-200/40 ${widthClass} max-w-full shadow-elegant scrollbar-hide z-40
    transition-[width] duration-300 h-screen overflow-y-auto shrink-0
    ${isOpen ? 'fixed top-0 bottom-0 left-0 translate-x-0' : 'fixed top-0 bottom-0 left-0 -translate-x-full'}
    md:static md:translate-x-0 ${mdWidthClass} md:z-30
  `;

  const hoveredNavItem =
    filteredNavigation.find((i: NavigationItem) => i.name === hoveredItem) ||
    null;

  const openFlyout = (itemName: string, rect: DOMRect) => {
    setHoveredItem(itemName);
    setFlyoutPos({ top: rect.top + window.scrollY, left: rect.right + 8 });
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };

  const scheduleCloseFlyout = () => {
    if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = window.setTimeout(() => {
      setHoveredItem(null);
      setFlyoutPos(null);
    }, 120);
  };

  return (
    <div className={sidebarClasses} aria-label='Sidebar navigation'>
      <div
        className={`${isCollapsed ? 'md:p-0' : 'p-4'} p-4 border-b border-slate-100 flex items-center justify-between`}
      >
        <div
          className={`flex items-center space-x-3 mx-auto ${isCollapsed ? 'md:hidden' : 'flex'}`}
        >
          <NavLink
            to={ROUTE_PATHS.DASHBOARD}
            aria-label='Navigate to Dashboard'
            className='cursor-pointer'
          >
            <img
              src={IMAGE_PATHS.logo}
              alt='Logo'
              className='w-auto h-10 mx-auto d-block'
            />
          </NavLink>
        </div>

        {/* Collapse/Expand toggle (hidden on mobile) */}
        <button
          type='button'
          className={`hidden md:inline-flex ${isCollapsed ? 'ml-auto mr-auto' : 'ml-2'}  p-2 rounded-full hover:bg-slate-100 focus:outline-none`}
          aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          onClick={() => setIsCollapsed(prev => !prev)}
        >
          <Menu className='h-6 w-6 text-slate-700' />
        </button>
        {/* Close button for mobile */}
        <button
          type='button'
          className='md:hidden ml-2 p-2 rounded-full hover:bg-slate-100 focus:outline-none'
          aria-label='Close sidebar'
          onClick={onClose}
        >
          <svg
            className='h-6 w-6 text-slate-700'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeWidth='2'
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>
      </div>

      <nav className='mt-4 px-3'>
        {/* Section: MENU */}
        <div className='mb-2'>
          <div
            className={` ${!isCollapsed ? 'px-4' : 'px-4 md:px-0 md:text-center'} py-2 text-xs font-semibold text-slate-400 tracking-widest`}
          >
            Menu
          </div>
          {filteredNavigation.map((item: NavigationItem) => (
            <div key={item.name} className='mb-1 relative'>
              {/* Desktop Collapsed View Only */}
              {isCollapsed && (
                <div className='hidden md:block'>
                  {item.children ? (
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className={`w-full flex items-center justify-center px-0 py-2 my-0.5 rounded-2xl transition-all duration-200 group ${
                        location.pathname === item.href ||
                        item.children.some((child: NavigationChild) =>
                          location.pathname.startsWith(child.href)
                        )
                          ? 'bg-blue-50 text-blue-700 shadow-sm'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      } `}
                      disabled={item?.disabled ?? false}
                      onMouseEnter={e =>
                        openFlyout(
                          item.name,
                          (
                            e.currentTarget as HTMLElement
                          ).getBoundingClientRect()
                        )
                      }
                      onMouseLeave={scheduleCloseFlyout}
                    >
                      {item.icon && (
                        <item.icon className='h-5 w-5 transition-transform group-hover:scale-110 flex-shrink-0' />
                      )}
                    </button>
                  ) : (
                    <NavLink
                      to={item.href}
                      onClick={e => {
                        if (item?.tabType) {
                          e.preventDefault();
                          const url =
                            item.href.startsWith('http') ||
                            item.href.startsWith('https')
                              ? item.href
                              : APP_BASE_URL + item.href;
                          window.open(url, item.tabType, 'noopener,noreferrer');
                        }
                      }}
                      className={({ isActive }) =>
                        `flex items-center justify-center px-0 py-2 my-0.5 rounded-2xl transition-all duration-200 group ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 shadow-sm'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`
                      }
                      end={item.href === ROUTE_PATHS.DASHBOARD}
                      onMouseEnter={e =>
                        openFlyout(
                          item.name,
                          (
                            e.currentTarget as HTMLElement
                          ).getBoundingClientRect()
                        )
                      }
                      onMouseLeave={scheduleCloseFlyout}
                    >
                      {item.icon && (
                        <item.icon className='h-5 w-5 transition-transform group-hover:scale-110 flex-shrink-0' />
                      )}
                    </NavLink>
                  )}
                </div>
              )}

              {/* Mobile View & Desktop Expanded View */}
              <div className={isCollapsed ? 'md:hidden' : 'block'}>
                {item.children ? (
                  <>
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className={`w-full flex items-center justify-between px-4 py-2 my-0.5 text-md font-medium rounded-2xl transition-all duration-200 group ${
                        location.pathname === item.href ||
                        item.children.some((child: NavigationChild) =>
                          location.pathname.startsWith(child.href)
                        )
                          ? 'bg-blue-50 text-blue-700 shadow-sm'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      } `}
                      disabled={item?.disabled ?? false}
                    >
                      <div className='flex items-center'>
                        {item.icon && (
                          <item.icon className='mr-3 h-4 w-4 transition-transform group-hover:scale-110 flex-shrink-0' />
                        )}
                        <span className='font-medium text-md text-nowrap'>
                          {item.name}
                        </span>
                      </div>
                      {expandedItem === item.name ? (
                        <ChevronDown className='h-5 w-5' />
                      ) : (
                        <ChevronRight className='h-5 w-5' />
                      )}
                    </button>
                    {expandedItem === item.name && (
                      <div className='ml-4 mt-1 mb-2'>
                        {item.children.map((child: NavigationChild) => (
                          <NavLink
                            key={child.name}
                            to={child.href}
                            onClick={e => {
                              if (child?.tabType) {
                                e.preventDefault();
                                const url =
                                  child.href.startsWith('http') ||
                                  child.href.startsWith('https')
                                    ? child.href
                                    : APP_BASE_URL + child.href;
                                window.open(
                                  url,
                                  child.tabType,
                                  'noopener,noreferrer'
                                );
                              }
                            }}
                            className={({ isActive }) =>
                              `flex items-center px-3 py-1.5 my-0.5 text-sm rounded-lg transition-all duration-200 ${
                                isActive
                                  ? 'bg-blue-100 text-blue-800 font-medium'
                                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                              }`
                            }
                            end={child.href === item.href}
                          >
                            <span className='ml-2 text-sm font-normal font-sans'>
                              {child.name}
                            </span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={item.href}
                    onClick={e => {
                      if (item?.tabType) {
                        e.preventDefault();
                        const url =
                          item.href.startsWith('http') ||
                          item.href.startsWith('https')
                            ? item.href
                            : APP_BASE_URL + item.href;
                        window.open(url, item.tabType, 'noopener,noreferrer');
                      }
                    }}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2 my-0.5 text-md font-medium rounded-2xl transition-all duration-200 group ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 shadow-sm'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`
                    }
                    end={item.href === ROUTE_PATHS.DASHBOARD}
                  >
                    {item.icon && (
                      <item.icon className='mr-3 h-4 w-4 transition-transform group-hover:scale-110 flex-shrink-0' />
                    )}
                    <span className='font-medium text-md text-nowrap'>
                      {item.name}
                    </span>
                  </NavLink>
                )}
              </div>
            </div>
          ))}

          <button
            className={`w-full flex items-center ${
              isCollapsed
                ? 'md:justify-center px-4 md:px-0'
                : 'justify-between px-4 text-md font-medium'
            } py-2 my-0.5 rounded-2xl transition-all duration-200 group text-slate-600 hover:bg-slate-50 hover:text-slate-900`}
            onClick={() => handleLogout()}
          >
            <div className='flex items-center'>
              <LogOut className='h-4 w-4 transition-transform group-hover:scale-110 flex-shrink-0' />
              <span
                className={`font-medium ml-3 text-md text-nowrap ${isCollapsed ? 'md:hidden' : 'block'}`}
              >
                Sign Out
              </span>
            </div>
          </button>
        </div>
      </nav>

      {isCollapsed &&
        hoveredItem &&
        flyoutPos &&
        hoveredNavItem &&
        createPortal(
          <div
            onMouseEnter={() => {
              if (hoverCloseTimer.current) {
                window.clearTimeout(hoverCloseTimer.current);
                hoverCloseTimer.current = null;
              }
            }}
            onMouseLeave={scheduleCloseFlyout}
            style={{
              position: 'fixed',
              top: flyoutPos.top,
              left: flyoutPos.left,
              zIndex: 1000
            }}
          >
            {hoveredNavItem.children ? (
              <div className='w-64 bg-white border border-slate-200 rounded-md shadow-xl p-2'>
                <div className='px-2 py-1.5 text-sm font-medium text-slate-700 flex items-center'>
                  {hoveredNavItem.icon && (
                    <hoveredNavItem.icon className='h-4 w-4 mr-2 text-slate-500' />
                  )}
                  {hoveredNavItem.name}
                </div>
                <div className='mt-1'>
                  {hoveredNavItem.children.map((child: NavigationChild) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      onClick={e => {
                        if (child?.tabType) {
                          e.preventDefault();
                          const url =
                            child.href.startsWith('http') ||
                            child.href.startsWith('https')
                              ? child.href
                              : APP_BASE_URL + child.href;
                          window.open(
                            url,
                            child.tabType,
                            'noopener,noreferrer'
                          );
                        }
                        setHoveredItem(null);
                        setFlyoutPos(null);
                      }}
                      className={({ isActive }) =>
                        `flex items-center px-3 py-1.5 my-0.5 text-sm rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-blue-100 text-blue-800 font-medium'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`
                      }
                      end={child.href === hoveredNavItem.href}
                    >
                      <span className='text-sm font-normal font-sans'>
                        {child.name}
                      </span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : (
              <div className='bg-white border border-slate-200 rounded-md shadow-xl px-2 py-1.5'>
                <div className='flex items-center text-sm text-slate-700'>
                  {hoveredNavItem.icon && (
                    <hoveredNavItem.icon className='h-4 w-4 mr-2 text-slate-500' />
                  )}
                  {hoveredNavItem.name}
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Sidebar;
